'use client';

import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { keyBy } from 'es-toolkit';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';

import { getBoardGameApi, getBoardGameMissionsApi, postBoardGameRollApi } from '@flipster/apis/v1/campaign';
import { GetBoardGameResponse, PostBoardGameRollResponse } from '@flipster/apis/v1/campaign/types';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';

import { CompetitionContentfulScope } from '../../event/competition/state';
import { authBunja } from '../../shared/auth/state';

export const rollAndMoveBunja = bunja(() => {
  const competitionContentful = bunja.use(CompetitionContentfulScope);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const store = bunja.use(JotaiStoreScope);

  const diceAndRollTab = competitionContentful?.eventTabsCollection?.items?.find((t) => t?.value === 'roll-and-dice');
  const rollAndDiceSectionLayout = diceAndRollTab?.tabReferencesCollection?.items?.find(
    (item) => item?.__typename === 'RollAndDiceSection',
  );

  const eventId = rollAndDiceSectionLayout ? competitionContentful?.slug : null;

  const isRollingAtom = atom(false);
  const showRewardAtom = atom(false);
  const lastRollResultAtom = atom<PostBoardGameRollResponse | null>(null);

  const queryKey = ['getBoardGameApi', eventId];

  const rollAndMoveQueryAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: [...queryKey, isLoggedIn],
      queryFn: () => getBoardGameApi({ boardGameTitle: eventId! }),
      select: (res: AxiosResponse<GetBoardGameResponse>) => res.data,
      refetchOnMount: true,
    };
  });

  const currentGameAtom = atom((get) => {
    const query = get(rollAndMoveQueryAtom);
    if (query.status !== 'success') return null;
    return query.data;
  });

  const isRegisteredAtom = atom((get) => {
    const game = get(currentGameAtom);
    return game?.isRegistered ?? false;
  });

  const previousRewardsAtom = atom<{ id: string; count: number }[]>([]);
  const isRewardsFrozenAtom = atom(false);
  const rewardsAtom = atom((get): { id: string; count: number }[] => {
    const isFrozen = get(isRewardsFrozenAtom);
    if (isFrozen) return get(previousRewardsAtom);

    const game = get(currentGameAtom);
    return game?.prizes ?? [];
  });

  const snapshotRewardsAtom = atom(null, (get, set) => {
    const game = get(currentGameAtom);
    set(previousRewardsAtom, game?.prizes ?? []);
    set(isRewardsFrozenAtom, true);
  });

  const unfreezeRewardsAtom = atom(null, (_get, set) => {
    set(isRewardsFrozenAtom, false);
  });

  const invalidRollAndMoveAtom = atom(null, (get) => {
    get(queryClientAtom).refetchQueries({ queryKey: ['getBoardGameApi'], type: 'active' });
  });

  const setNewRollResultAtom = atom(null, (get, set, response: PostBoardGameRollResponse) => {
    const queryClient = get(queryClientAtom);
    const isLoggedIn = get(isCookieLoggedInAtom);

    set(lastRollResultAtom, response);

    queryClient.setQueryData<AxiosResponse<GetBoardGameResponse>>([...queryKey, isLoggedIn], (prev) => {
      if (!prev) {
        set(invalidRollAndMoveAtom);
        return prev;
      }

      let prizes = prev.data.prizes;
      if (response.prizeId) {
        prizes = [...prev.data.prizes];
        const index = prizes.findIndex((p) => p.id === response.prizeId);
        if (index >= 0) {
          prizes[index] = {
            ...prizes[index],
            count: prizes[index].count + 1,
          };
        } else {
          prizes.push({ id: response.prizeId, count: 1 });
        }
      }

      return {
        ...prev,
        data: {
          ...prev.data,
          currentPosition: response.positionTo,
          remainingRolls: Math.max(prev.data.remainingRolls - 1, 0),
          prizes,
        },
      };
    });
  });

  const rollDiceMutationAtom = atomWithMutation(() => ({
    mutationFn: async () => {
      if (!eventId) throw new Error('No game found');
      const { data } = await postBoardGameRollApi({ boardGameTitle: eventId });
      return data;
    },
    onSuccess(response: PostBoardGameRollResponse) {
      store.set(setNewRollResultAtom, response);
    },
    onError(error: Error) {
      store.set(isRollingAtom, false);
      snackbar.error({ message: getErrorMessage(error) });
    },
  }));

  return {
    eventId,
    rollAndDiceSectionLayout,
    currentGameAtom,
    isRegisteredAtom,
    rewardsAtom,
    isRollingAtom,
    showRewardAtom,
    snapshotRewardsAtom,
    unfreezeRewardsAtom,
    lastRollResultAtom,
    rollDiceMutationAtom,
    invalidRollAndMoveAtom,
    setNewRollResultAtom,
  };
});

export const rollAndMoveMissionsBunja = bunja(() => {
  const { eventId, rollAndDiceSectionLayout } = bunja.use(rollAndMoveBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const missionsLayout =
    rollAndDiceSectionLayout?.__typename === 'RollAndDiceSection'
      ? rollAndDiceSectionLayout.missionsCollection?.items
      : null;

  const missionsQueryAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getBoardGameMissionsApi', eventId, isLoggedIn],
      queryFn: () => getBoardGameMissionsApi({ boardGameTitle: eventId! }),
      select: (res: Awaited<ReturnType<typeof getBoardGameMissionsApi>>) => keyBy(res.data.missions ?? [], (m) => m.id),
      refetchOnMount: true,
    };
  });

  const missionsAtom = atom((get) => {
    const query = get(missionsQueryAtom);
    if (!query.data) return null;
    return query.data;
  });

  return { eventId, missionsAtom, missionsLayout };
});
